// Rendu d'une question de la page publique selon son type (contrat : QuestionRenduProps dans ./props.ts).
// Adapté de « adminvisastravelapp/src/formulaires/QuestionRenduAdmin.tsx » (aperçu « Tester » du constructeur,
// MUI 5) : mêmes 9 types, mêmes valeurs (chaîne, ou tableau de chaînes pour choix_multiple, jamais de nombre),
// mais en MUI 6 (slotProps), avec un vrai dépôt de fichiers (ZoneDepot) et en mobile-first (champs pleine largeur,
// taille medium pour le tactile).
//  - texte : TextField (type email / tel selon `format`) ; texte_long : multiline ; nombre : type=number (valeur
//    conservée en chaîne : c'est valider() qui contrôle le format et les bornes) ; date : type=date, libellé rétréci ;
//  - oui_non : RadioGroup Oui / Non ; choix : RadioGroup si ≤ 5 options sinon Select ; choix_multiple : Checkbox ;
//  - fichier : ZoneDepot (onFichiers / onErreurFichier relayés) ; info : Alert info (libellé + aide, jamais de valeur).
// `aide` en helperText ; `obligatoire` → required (astérisque) ; `erreur` → error + helperText (prend le pas sur l'aide).
// Chaque question est enveloppée d'un <div id="question-<id>"> pour que l'écran puisse y défiler (première erreur).
import React from 'react';
import {
  Alert, Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, InputLabel, MenuItem,
  Radio, RadioGroup, Select, TextField, Typography,
} from '@mui/material';
import type { QuestionRenduProps } from './props';
import type { TypeQuestion } from './types';
import ZoneDepot from './ZoneDepot';

// Au-delà de ce nombre d'options, un choix unique passe du RadioGroup au Select (plan §3.2)
const MAX_OPTIONS_RADIO = 5;

// Plafond de saisie par type (celui de nettoyer côté moteur) quand la question ne fixe pas de `max`
const LONGUEUR_MAX: Partial<Record<TypeQuestion, number>> = { texte: 2000, texte_long: 5000 };

const QuestionRendu: React.FC<QuestionRenduProps> = ({
  jeton, question: q, valeur, fichiers, erreur, disabled = false, onChange, onFichiers, onErreurFichier, onTransfert,
}) => {
  const texte = typeof valeur === 'string' ? valeur : '';
  const liste = Array.isArray(valeur) ? valeur : [];
  const aide = q.aide && q.aide.trim() ? q.aide.trim() : undefined;
  const helper = erreur || aide;
  const options = q.options || [];
  const idChamp = `champ-${q.id}`;
  const idLibelle = `libelle-${q.id}`;

  let contenu: React.ReactNode;

  switch (q.type) {
    case 'info':
      // Paragraphe d'information : jamais de réponse
      contenu = (
        <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { minWidth: 0 } }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{q.libelle}</Typography>
          {aide && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {aide}
            </Typography>
          )}
        </Alert>
      );
      break;

    case 'texte':
    case 'texte_long': {
      const email = q.type === 'texte' && q.format === 'email';
      const tel = q.type === 'texte' && q.format === 'telephone';
      contenu = (
        <TextField
          id={idChamp}
          name={q.id}
          label={q.libelle}
          required={q.obligatoire}
          disabled={disabled}
          fullWidth
          type={email ? 'email' : tel ? 'tel' : 'text'}
          multiline={q.type === 'texte_long'}
          minRows={q.type === 'texte_long' ? 3 : undefined}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
          error={!!erreur}
          helperText={helper}
          slotProps={{
            htmlInput: {
              maxLength: q.max || LONGUEUR_MAX[q.type],
              inputMode: email ? 'email' : tel ? 'tel' : undefined,
              autoComplete: email ? 'email' : tel ? 'tel' : 'off',
            },
          }}
        />
      );
      break;
    }

    case 'nombre':
      contenu = (
        <TextField
          id={idChamp}
          name={q.id}
          label={q.libelle}
          required={q.obligatoire}
          disabled={disabled}
          fullWidth
          type="number"
          value={texte}
          onChange={(e) => onChange(e.target.value)}
          error={!!erreur}
          helperText={helper}
          slotProps={{ htmlInput: { min: q.min, max: q.max, step: 'any', inputMode: 'decimal' } }}
        />
      );
      break;

    case 'date':
      contenu = (
        <TextField
          id={idChamp}
          name={q.id}
          label={q.libelle}
          required={q.obligatoire}
          disabled={disabled}
          fullWidth
          type="date"
          value={texte}
          onChange={(e) => onChange(e.target.value)}
          error={!!erreur}
          helperText={helper}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );
      break;

    case 'oui_non':
      contenu = (
        <FormControl component="fieldset" required={q.obligatoire} disabled={disabled} error={!!erreur} fullWidth>
          <FormLabel component="legend" id={idLibelle}>{q.libelle}</FormLabel>
          <RadioGroup row name={q.id} aria-labelledby={idLibelle} value={texte} onChange={(e) => onChange(e.target.value)}>
            <FormControlLabel value="oui" control={<Radio />} label="Oui" />
            <FormControlLabel value="non" control={<Radio />} label="Non" />
          </RadioGroup>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      );
      break;

    case 'choix':
      if (options.length <= MAX_OPTIONS_RADIO) {
        contenu = (
          <FormControl component="fieldset" required={q.obligatoire} disabled={disabled} error={!!erreur} fullWidth>
            <FormLabel component="legend" id={idLibelle}>{q.libelle}</FormLabel>
            <RadioGroup name={q.id} aria-labelledby={idLibelle} value={texte} onChange={(e) => onChange(e.target.value)}>
              {options.map((o) => (
                <FormControlLabel key={o.valeur} value={o.valeur} control={<Radio />} label={o.libelle || o.valeur} />
              ))}
            </RadioGroup>
            {helper && <FormHelperText>{helper}</FormHelperText>}
          </FormControl>
        );
      } else {
        contenu = (
          <FormControl required={q.obligatoire} disabled={disabled} error={!!erreur} fullWidth>
            <InputLabel id={idLibelle}>{q.libelle}</InputLabel>
            <Select
              id={idChamp}
              name={q.id}
              labelId={idLibelle}
              label={q.libelle}
              value={texte}
              onChange={(e) => onChange(String(e.target.value))}
            >
              <MenuItem value=""><em>Aucune réponse</em></MenuItem>
              {options.map((o) => (
                <MenuItem key={o.valeur} value={o.valeur}>{o.libelle || o.valeur}</MenuItem>
              ))}
            </Select>
            {helper && <FormHelperText>{helper}</FormHelperText>}
          </FormControl>
        );
      }
      break;

    case 'choix_multiple': {
      // Coche / décoche une option : l'ordre du tableau est celui des clics (le moteur ne regarde que l'appartenance)
      const basculer = (v: string, coche: boolean) =>
        onChange(coche ? [...liste.filter((x) => x !== v), v] : liste.filter((x) => x !== v));
      contenu = (
        <FormControl component="fieldset" required={q.obligatoire} disabled={disabled} error={!!erreur} fullWidth>
          <FormLabel component="legend" id={idLibelle}>{q.libelle}</FormLabel>
          <FormGroup aria-labelledby={idLibelle}>
            {options.map((o) => (
              <FormControlLabel
                key={o.valeur}
                control={
                  <Checkbox
                    name={q.id}
                    value={o.valeur}
                    checked={liste.indexOf(o.valeur) >= 0}
                    onChange={(e) => basculer(o.valeur, e.target.checked)}
                  />
                }
                label={o.libelle || o.valeur}
              />
            ))}
          </FormGroup>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      );
      break;
    }

    case 'fichier':
      contenu = (
        <FormControl component="fieldset" required={q.obligatoire} disabled={disabled} error={!!erreur} fullWidth>
          <FormLabel component="legend" id={idLibelle} sx={{ mb: 1 }}>{q.libelle}</FormLabel>
          <ZoneDepot
            jeton={jeton}
            question={q}
            fichiers={fichiers}
            disabled={disabled}
            onChange={onFichiers}
            onErreur={onErreurFichier}
            onTransfert={onTransfert}
          />
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      );
      break;

    default:
      contenu = null;
  }

  return (
    <Box id={`question-${q.id}`} data-question={q.id} sx={{ width: '100%', minWidth: 0 }}>
      {contenu}
    </Box>
  );
};

export default QuestionRendu;
